/**
 * The app's active display locale, held as a one-value registry rather than
 * read from the i18n singleton at the point of use.
 *
 * Why the indirection: `dateUtils.ts` needs the language the user picked in
 * order to format timestamps, but importing `../i18n` to get it drags
 * i18next's type declarations into every scoped `tsc` run that touches the
 * util. Those declarations fail to PARSE under this repo's TypeScript
 * (`node_modules/i18next/typescript/t.d.ts` — TS1139/TS1005), and a parse
 * error aborts semantic checking for the whole compile, which is exactly why
 * `gchat/CLAUDE.md` calls the repo-wide type gate non-diagnostic. Components
 * already live with that; there is no reason to spread it into a util layer
 * that is otherwise dependency-free and cheaply verifiable.
 *
 * This module imports nothing. `src/i18n.ts` pushes the value in on init and
 * on every `languageChanged`, so the coupling points from the i18n layer
 * toward the utils, never the reverse.
 */

let activeLocale: string | undefined;

/** Called by `src/i18n.ts` on init and on every language change. */
export function setActiveLocale(locale: string | undefined): void {
  activeLocale = locale || undefined;
}

/**
 * The locale display should follow. Falls back to the browser's language
 * only before i18n has initialised — in a browser that is a narrow window,
 * and in a unit test it means a caller that passes no explicit locale gets
 * deterministic behaviour rather than whatever another test left behind.
 */
export function getActiveLocale(): string {
  return activeLocale || navigator.language;
}

/** Test seam: forget any pushed value and fall back to the browser again. */
export function resetActiveLocale(): void {
  activeLocale = undefined;
}
